import { useState, useEffect, useRef } from 'react';
import { Phone, Mail, Globe, MapPin, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const CONTACT_ENDPOINT = '/api/contact';

const contactInfo = [
  { icon: Phone, label: 'Phone', value: '+6014-3518576' },
  { icon: Mail, label: 'Email', value: 'hello@hiresphere.co' },
  { icon: Globe, label: 'Website', value: 'www.hiresphere.co' },
  { icon: MapPin, label: 'Location', value: 'Kuala Lumpur, Malaysia' },
];

export default function ContactSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: '',
  });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(CONTACT_ENDPOINT, formData);
      if (response.data?.success) {
        toast.success(response.data.message);
        setFormData({ name: '', company: '', email: '', message: '' });
      } else {
        toast.error(response.data?.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message;
      toast.error(serverMessage || 'Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      data-testid="contact-section"
      className="py-24 sm:py-32 bg-[#0D1321] relative overflow-hidden"
    >
      {/* Circuit Board Texture Overlay */}
      <img
        src="https://static.prod-images.emergentagent.com/jobs/651541d7-5e6b-42ae-bbbb-a15776d2b14a/images/e4999b22e6e96b63f63379e586d4e089214cb10290beac1b50287d8c22ef2507.png"
        alt=""
        loading="lazy"
        decoding="async"
        className="circuit-overlay"
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        {/* CTA Header */}
        <div className={`text-center mb-16 reveal ${isVisible ? 'visible' : ''}`}>
          <p className="text-[#F5A623] text-xs tracking-[0.2em] uppercase font-semibold mb-4">
            Get in Touch
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-white mb-4">
            Ready to build something intelligent?
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Let's discuss how HireSphere can help transform your business with cutting-edge technology solutions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className={`reveal ${isVisible ? 'visible' : ''}`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-white/70 text-sm font-medium mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    data-testid="contact-name-input"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:border-[#F5A623] transition-colors"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-white/70 text-sm font-medium mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    data-testid="contact-company-input"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:border-[#F5A623] transition-colors"
                    placeholder="Your company"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-white/70 text-sm font-medium mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="contact-email-input"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:border-[#F5A623] transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-white/70 text-sm font-medium mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  data-testid="contact-message-input"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:border-[#F5A623] transition-colors resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="btn-accent rounded-full px-8 py-4 text-base font-semibold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Start a Conversation
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className={`reveal ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '200ms' }}>
            <div className="glass-card rounded-2xl p-8 mb-8">
              <h3 className="text-xl font-semibold text-white mb-6">Contact Information</h3>
              <div className="space-y-6">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#F5A623]/20 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-[#F5A623]" />
                    </div>
                    <div>
                      <p className="text-white/50 text-sm">{item.label}</p>
                      <p className="text-white font-medium">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
